import { boundingExtent, containsCoordinate } from 'ol/extent';
import { fromLonLat, toLonLat } from 'ol/proj';

import { OL_RESTRICTED_BOUNDS, OL_VWORLD_BOUNDS } from '@/config';
import CoordUtils from '@/utils/CoordUtils';

/**
 * 지도 관련 유틸리티 함수들
 */
class MapUtils {
  /**
   * 맵 제한 영역 계산
   * @returns {Array|undefined} extent 또는 undefined
   */
  static getExtent() {
    // Restricting Map Bounds
    if (process.env.REACT_APP_USE_MAP_RESTRICTION === 'true') {
      return [
        ...fromLonLat([OL_RESTRICTED_BOUNDS.west, OL_RESTRICTED_BOUNDS.south]),
        ...fromLonLat([OL_RESTRICTED_BOUNDS.east, OL_RESTRICTED_BOUNDS.north]),
      ];
    }
  }

  /**
   * VWorld 맵 영역 계산
   * @returns {Array} VWorld extent
   */
  static getVWorldExtent() {
    return [
      ...fromLonLat([OL_VWORLD_BOUNDS.west, OL_VWORLD_BOUNDS.south]),
      ...fromLonLat([OL_VWORLD_BOUNDS.east, OL_VWORLD_BOUNDS.north]),
    ];
  }

  /**
   * 현재 맵이 VWorld인지 확인
   * @param {string} currentMapId - 현재 맵 ID
   * @returns {boolean}
   */
  static isVWorldMap(currentMapId) {
    return currentMapId === 'VWORLD';
  }

  /**
   * 좌표가 VWorld 영역 내에 있는지 확인
   * @param {Array} position - [lng, lat] 좌표
   * @returns {boolean}
   */
  static isInsideVWorld(position) {
    const extent = [OL_VWORLD_BOUNDS.west, OL_VWORLD_BOUNDS.south, OL_VWORLD_BOUNDS.east, OL_VWORLD_BOUNDS.north];
    return containsCoordinate(extent, position);
  }

  /**
   * 여러 위치를 포함하는 bounds로 맵 피팅
   * @param {Object} map - OpenLayers 맵 인스턴스
   * @param {Array} positions - [[lng, lat], ...] 좌표 배열
   */
  static fitBounds(map, positions) {
    // 위치 갯수 1개인 경우
    if (positions.length === 1) {
      map.getView().setCenter(fromLonLat(positions[0]));
      map.getView().setZoom(15);
    }
    // 위치 갯수 1개 초과인 경우
    else if (positions.length > 1) {
      const coordinates = positions.map((position) => fromLonLat(position));
      const extent = boundingExtent(coordinates);
      map.getView().fit(extent, { padding: [120, 120, 120, 120] });
    }
  }

  /**
   * 맵 중심 좌표 가져오기
   * @param {Object} map - OpenLayers 맵 인스턴스
   * @returns {Object} { lat, lng }
   */
  static getCenter(map) {
    const center = map.getView().getCenter();
    return CoordUtils.objectFromArray(toLonLat(center));
  }

  /**
   * 맵 중심 좌표 설정
   * @param {Object} map - OpenLayers 맵 인스턴스
   * @param {Object} position - { lat, lng }
   * @param {Object} options - { duration: number }
   */
  static setCenter(map, position, options = { duration: 0 }) {
    map.getView().animate({
      center: fromLonLat(CoordUtils.arrayFromObject(position)),
      duration: options.duration,
    });
  }
}

export default MapUtils;
